import { apiErrorSchema, type ErrorCode } from '@trackflow/shared';
import { isEmbedded, shopifyIdToken } from './embedded';

/** A failed API call, carrying the server's stable error code and user-safe message. */
export class ApiRequestError extends Error {
  constructor(
    public readonly status: number,
    public readonly code: ErrorCode | 'NETWORK_ERROR',
    message: string,
    public readonly details: { path: string; message: string }[] = [],
    public readonly requestId?: string,
  ) {
    super(message);
    this.name = 'ApiRequestError';
  }
}

interface RequestOptions extends Omit<RequestInit, 'body'> {
  body?: unknown;
}

// The CSRF token comes from /auth/me (and login/register). Kept in memory only — never in
// localStorage — and sent as a header on every state-changing request.
let csrfToken: string | undefined;
export function setCsrfToken(token: string | undefined) {
  csrfToken = token;
}

let onUnauthenticated: (() => void) | undefined;
/** Called when a request fails with 401 (session expired) so the app can route to login. */
export function setUnauthenticatedHandler(fn: () => void) {
  onUnauthenticated = fn;
}

/**
 * fetch wrapper for the TrackFlow API. Same-origin, cookies included, JSON in and out.
 * Every failure becomes an ApiRequestError so UI code handles one shape.
 */
export async function api<T>(
  path: string,
  { body, headers, method = 'GET', ...init }: RequestOptions = {},
): Promise<T> {
  let res: Response;
  // Inside Shopify admin: authenticate every call with a fresh App Bridge ID token (no cookies
  // — third-party iframes can't rely on them). Standalone: session cookie + CSRF header.
  const idToken = isEmbedded() ? await shopifyIdToken() : null;
  try {
    res = await fetch(`/api${path}`, {
      credentials: idToken ? 'omit' : 'same-origin',
      method,
      ...init,
      headers: {
        Accept: 'application/json',
        ...(body !== undefined && { 'Content-Type': 'application/json' }),
        ...(idToken && { Authorization: `Bearer ${idToken}` }),
        ...(!idToken && method !== 'GET' && csrfToken && { 'X-CSRF-Token': csrfToken }),
        ...headers,
      },
      body: body === undefined ? undefined : JSON.stringify(body),
    });
  } catch {
    throw new ApiRequestError(
      0,
      'NETWORK_ERROR',
      "Can't reach TrackFlow. Check your connection and try again.",
    );
  }

  if (res.status === 204) return undefined as T;
  const payload: unknown = await res.json().catch(() => null);

  if (!res.ok) {
    const parsed = apiErrorSchema.safeParse(payload);
    const error = parsed.success
      ? new ApiRequestError(
          res.status,
          parsed.data.error.code,
          parsed.data.error.message,
          parsed.data.error.details,
          parsed.data.error.requestId,
        )
      : new ApiRequestError(
          res.status,
          'INTERNAL_ERROR',
          'Something went wrong. Please try again.',
        );
    if (res.status === 401 && !path.startsWith('/auth/') && !isEmbedded()) onUnauthenticated?.();
    throw error;
  }
  return payload as T;
}

export const apiPost = <T>(path: string, body?: unknown) => api<T>(path, { method: 'POST', body });
export const apiPatch = <T>(path: string, body?: unknown) =>
  api<T>(path, { method: 'PATCH', body });
export const apiPut = <T>(path: string, body?: unknown) => api<T>(path, { method: 'PUT', body });
export const apiDelete = <T>(path: string) => api<T>(path, { method: 'DELETE' });

/** Build a query string, skipping empty values. */
export function qs(params: Record<string, string | number | boolean | undefined | null>) {
  const search = new URLSearchParams();
  for (const [k, v] of Object.entries(params)) {
    if (v !== undefined && v !== null && v !== '') search.set(k, String(v));
  }
  const s = search.toString();
  return s ? `?${s}` : '';
}
